import styled from "@emotion/styled";

export const debugVars = {
  bg: "#ffffff",
  bgLight: "#f5f5f7",
  border: "#e0e0e0",
  text: "#1a1a2e",
  textMuted: "#666",
  accent: "#4a4aff",
  success: "#22c55e",
  danger: "#dc2626",
  fontSize: "14px",
  fontSizeSmall: "12px",
  touchTarget: "44px",
  radius: "8px"
} as const;

export const DebugContainer = styled.div`
  label: DebugContainer;
  all: revert;

  position: absolute;
  inset: 0;
  background-color: ${debugVars.bg};
  padding: calc(var(--safe-area-inset-top, 0px) + 1rem) 1rem
    calc(var(--safe-area-inset-bottom, 0px) + 1rem);
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial,
    sans-serif;
  font-size: ${debugVars.fontSize};
  font-weight: 400;
  line-height: 1.5;
  color: ${debugVars.text};
  max-width: 100vw;
  overflow: auto;
  z-index: 999999999;
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  input:not([type="checkbox"]) {
    all: revert;
    padding: 0.5rem;
    border: 1px solid ${debugVars.border};
    border-radius: 4px;
    font-family: inherit;
    font-size: ${debugVars.fontSize};
    width: 100%;
    box-sizing: border-box;
  }

  select {
    all: revert;
    padding: 0.5rem;
    border: 1px solid ${debugVars.border};
    border-radius: 4px;
    font-family: inherit;
    font-size: ${debugVars.fontSize};
    background: white;
  }

  textarea {
    all: revert;
    padding: 0.5rem;
    border: 1px solid ${debugVars.border};
    border-radius: 4px;
    font-family: inherit;
    font-size: ${debugVars.fontSize};
    width: 100%;
    box-sizing: border-box;
    min-height: 80px;
    resize: vertical;
  }

  &.toggle {
    height: 0;
    width: 0;
    padding: 0;
    left: auto;
    overflow: hidden;
    background: transparent;
  }
`;

export const DebugToggle = styled.div`
  all: revert;
  position: fixed;
  top: 80px;
  right: 8px;
  opacity: 0.3;
  width: ${debugVars.touchTarget};
  height: ${debugVars.touchTarget};
  background: linear-gradient(135deg, #ff9500, #ff6b00);
  z-index: 99999999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${debugVars.radius};

  &::after {
    content: "≡";
    font-size: 20px;
    color: white;
    font-weight: bold;
  }
`;

export const DebugSectionContainer = styled.details`
  all: revert;
  margin-top: 0.5rem;

  summary {
    all: revert;
    cursor: pointer;
    padding: 0.75rem 1rem;
    min-height: ${debugVars.touchTarget};
    background-color: ${debugVars.bgLight};
    border: 1px solid ${debugVars.border};
    border-radius: ${debugVars.radius};
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: ${debugVars.fontSize};
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    &::before {
      content: "▶";
      margin-right: 0.75rem;
      font-size: 10px;
      transition: transform 0.2s ease;
      color: ${debugVars.textMuted};
    }
  }

  &[open] summary {
    border-radius: ${debugVars.radius} ${debugVars.radius} 0 0;

    &::before {
      transform: rotate(90deg);
    }
  }

  .content {
    padding: 1rem;
    border: 1px solid ${debugVars.border};
    border-top: none;
    border-radius: 0 0 ${debugVars.radius} ${debugVars.radius};
    background-color: ${debugVars.bg};
  }
`;

export const DebugCategoryHeader = styled.h3`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${debugVars.textMuted};
  margin: 1.5rem 0 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${debugVars.border};

  &:first-of-type {
    margin-top: 0.5rem;
  }
`;

export const DebugTable = styled.table`
  all: revert;
  width: 100%;
  border-collapse: collapse;
  font-size: ${debugVars.fontSizeSmall};

  th,
  td {
    border: 1px solid ${debugVars.border};
    padding: 0.5rem;
    text-align: left;
    vertical-align: top;
  }

  th {
    background-color: ${debugVars.bgLight};
    font-weight: 600;
    font-size: 11px;
    white-space: nowrap;
  }

  tr:nth-of-type(even) {
    background-color: ${debugVars.bgLight};
  }

  code {
    font-size: 10px;
    word-break: break-all;
  }
`;

export const DebugButton = styled.button`
  all: revert;
  min-height: ${debugVars.touchTarget};
  padding: 0.5rem 1rem;
  font-family: inherit;
  font-size: ${debugVars.fontSizeSmall};
  font-weight: 500;
  border: 1px solid ${debugVars.border};
  border-radius: ${debugVars.radius};
  background-color: ${debugVars.bgLight};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${debugVars.border};
  }

  &:active {
    background-color: #d0d0d0;
  }

  &.danger {
    color: ${debugVars.danger};
    border-color: ${debugVars.danger};

    &:hover {
      background-color: #fef2f2;
    }
  }

  &.primary {
    background-color: ${debugVars.accent};
    color: white;
    border-color: ${debugVars.accent};

    &:hover {
      background-color: #3a3aef;
    }
  }

  &.small {
    min-height: 32px;
    padding: 0.25rem 0.75rem;
    font-size: 11px;
  }
`;

export const DebugInfoBox = styled.div<{
  variant?: "warning" | "info" | "success";
}>`
  padding: 0.75rem 1rem;
  border-radius: ${debugVars.radius};
  font-size: 13px;
  line-height: 1.5;

  ${({ variant }) => {
    switch (variant) {
      case "warning":
        return `
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
        `;
      case "success":
        return `
          background-color: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #166534;
        `;
      default:
        return `
          background-color: ${debugVars.bgLight};
          border: 1px solid ${debugVars.border};
          color: ${debugVars.text};
        `;
    }
  }}

  p {
    margin: 0.25rem 0;

    &:first-of-type {
      margin-top: 0;
    }

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  code {
    background: rgba(0, 0, 0, 0.05);
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    font-size: 12px;
  }
`;

export const DebugCheckbox = styled.label`
  all: revert;
  display: flex;
  align-items: center;
  min-height: ${debugVars.touchTarget};
  cursor: pointer;
  gap: 0.75rem;
  padding: 0.25rem 0;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${debugVars.accent};
  }

  span {
    font-size: ${debugVars.fontSize};
  }
`;

export const DebugKeyValue = styled.div`
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${debugVars.border};
  gap: 0.5rem;
  align-items: flex-start;

  &:last-child {
    border-bottom: none;
  }

  .key {
    font-weight: 600;
    font-size: ${debugVars.fontSizeSmall};
    color: ${debugVars.textMuted};
    min-width: 100px;
    flex-shrink: 0;
  }

  .value {
    font-size: ${debugVars.fontSize};
    word-break: break-word;
    flex: 1;

    &.mono {
      font-family: monospace;
      font-size: ${debugVars.fontSizeSmall};
    }

    &.truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &.success {
      color: ${debugVars.success};
    }

    &.danger {
      color: ${debugVars.danger};
    }

    &.muted {
      color: ${debugVars.textMuted};
    }
  }
`;

export const DebugToggleSwitch = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${debugVars.touchTarget};
  padding: 0.5rem 0;
  cursor: pointer;
  border-bottom: 1px solid ${debugVars.border};

  &:last-child {
    border-bottom: none;
  }

  .label {
    font-size: ${debugVars.fontSizeSmall};
    flex: 1;
    padding-right: 1rem;
  }

  .switch {
    position: relative;
    width: 48px;
    height: 28px;
    background-color: ${debugVars.border};
    border-radius: 14px;
    transition: background-color 0.2s;
    flex-shrink: 0;

    &::after {
      content: "";
      position: absolute;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: white;
      top: 2px;
      left: 2px;
      transition: transform 0.2s;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }

  input {
    display: none;

    &:checked + .switch {
      background-color: ${debugVars.success};

      &::after {
        transform: translateX(20px);
      }
    }
  }
`;

export const DebugFormGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: ${debugVars.fontSizeSmall};
    font-weight: 500;
    color: ${debugVars.textMuted};
    margin-bottom: 0.25rem;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DebugFormRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;

  label {
    font-size: ${debugVars.fontSizeSmall};
    font-weight: 500;
    min-width: 70px;
    color: ${debugVars.textMuted};
  }

  input,
  select {
    flex: 1;
  }
`;

export const DebugButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DebugDivider = styled.hr`
  border: none;
  border-top: 1px solid ${debugVars.border};
  margin: 1rem 0;
`;

export const DebugBadge = styled.span<{
  variant?: "success" | "danger" | "warning" | "info";
}>`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;

  ${({ variant }) => {
    switch (variant) {
      case "success":
        return `background: #dcfce7; color: #166534;`;
      case "danger":
        return `background: #fee2e2; color: #991b1b;`;
      case "warning":
        return `background: #fef3c7; color: #92400e;`;
      default:
        return `background: #e0e7ff; color: #3730a3;`;
    }
  }}
`;
